"use client"

import { useState, createContext, ReactNode, useContext, useEffect, useCallback, useMemo, useRef, MutableRefObject } from "react"
import type { Workspace, Members, Notifications, WorkspaceGroup } from "@/types/workspace";
import { fetchWorkspaces } from "@/services-frontend/workspace/workspaceServices";
import { getWithAuth } from "@/lib/api-helpers";
import { createGroup, updateGroup, deleteGroup } from "@/services-frontend/workspace/groupServices";
import { useAuth } from "@/hooks/use-auth";
import CryptoJS from "crypto-js";
import { useNotificationsContext } from "./notification/notificationContext";
import { toast } from "@/lib/toast";
import { usePathname, useRouter } from "next/navigation";
import { clearWorkspaceIDBData } from "@/indexedDB";
import { eventBus } from "@/services-frontend/comment/eventBus";

const SECRET_KEY = process.env.NEXT_PUBLIC_CJS_TOKEN;

// Raw member shape from GET /api/user/workspace/:id/members. Mapped into the
// unified `Members` type before being stored.
type ApiMember = {
    id?: string;
    name?: string;
    email?: string;
    image?: string;
    about?: string;
    coverUrl?: string;
    role?: string;
    joinedAt?: string;
};

type workspaceContextType = {
    name: string
    workspaces: Workspace[];
    fetchAllWorkspace: () => Promise<void>;
    setCurrentWorkspace: (workspace: Workspace | null) => void;
    handleSelectedWorkspace: (workspace: Workspace) => void;
    switchWorkspace: (workspace: Workspace, destination?: string) => void;
    /** Where the notes layout should navigate once the next switch settles (null = its default). */
    pendingSwitchDestinationRef: MutableRefObject<string | null>;
    workspaceMembers: Members[];
    /** Resolve a member's profile image URL by userId or email. */
    getMemberImage: (idOrEmail?: string) => string | undefined;
    /** Resolve a member's current display name by userId or email. */
    getMemberName: (idOrEmail?: string) => string | undefined;
    /** Resolve a member's userId by userId or email (e.g. for profile links). */
    getMemberId: (idOrEmail?: string) => string | undefined;
    workspaceNotifications: Notifications[];
    allWorkspaceMemberName: string[]
    currentWorkspace: Workspace | null
    workspaceGroups: WorkspaceGroup[];
    refreshWorkspaceGroups: () => void;
    createWorkspaceGroup: (name: string, members?: Members[]) => Promise<WorkspaceGroup | null>;
    updateWorkspaceGroup: (groupId: string, name?: string, members?: Members[]) => Promise<void>;
    deleteWorkspaceGroup: (groupId: string) => Promise<void>;
}

const WorkspaceContext = createContext<workspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
    const pathname = usePathname();
    const isFormPage = pathname?.startsWith('/form/');

    const router = useRouter();
    const [name, setName] = useState<string>('')
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
    // Kept current on every render (assigned below) so the live-membership
    // listener can read them without re-subscribing on each change.
    const currentWorkspaceRef = useRef<Workspace | null>(null);
    const switchWorkspaceRef = useRef<((w: Workspace, destination?: string) => void) | null>(null);
    // Destination the notes layout navigates to after the next switch settles.
    const pendingSwitchDestinationRef = useRef<string | null>(null);
    // Members come from the workspace doc, then get enriched in place with
    // profile data (image/about/cover/org) once the members API responds.
    const [workspaceMembers, setWorkspaceMembers] = useState<Members[]>([]);
    const [workspaceNotifications, setWorkspaceNotifications] = useState<Notifications[]>([]);
    const [workspaceGroups, setWorkspaceGroups] = useState<WorkspaceGroup[]>([]);

    // Auth context must be used inside the provider component
    const { isAuthenticated, user } = useAuth();

    function getWorkspaceIdFromCookie(): string | null {
        const match = document.cookie.match(new RegExp("(^| )workspace=([^;]+)"));
        if (!match || !match[2]) return null;

        try {
            const decrypted = CryptoJS.AES.decrypt(decodeURIComponent(match[2]), SECRET_KEY);
            const parsed = JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));
            return parsed.workspaceId;
        } catch (err) {
            console.error("Failed to decrypt workspace cookie:", err);
            return null;
        }
    }

    const handleRealTimeNotification = async (workspaceId: string) => {
        if (!workspaceId) return;

        const eventSource = new EventSource(
            `/api/work-space/notification`
        );

        eventSource.onopen = () => {
            console.log("SSE connection opened");
        };

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                setWorkspaceNotifications((prev) => [
                    data.notification,
                    ...prev, // prepend so newest shows first
                ]);
            } catch (err) {
                console.error("Failed to parse SSE notification", err);
            }
        };

        eventSource.onerror = (err) => {
            console.error("SSE connection error:", err);
            eventSource.close();
        };

        return () => {
            console.log(" Closing SSE connection");
            eventSource.close();
        };
    }


    const handleSelectedWorkspace = (workspace: Workspace) => {
        try {
            // const selectedWorkspace = workspaces.find((ws) => ws._id === workspaceId)
            if (workspace) {
                // Clear the sidebar order from IDB so the previous workspace's
                // page order doesn't show until the new workspace's data loads.
                if (currentWorkspace?._id && currentWorkspace._id !== workspace._id) {
                    clearWorkspaceIDBData();
                }

                setCurrentWorkspace(workspace);

                // ✅ set notifications from workspace data
                setWorkspaceNotifications(workspace.notifications || []);

                // ✅ Per-tab workspace identity — sessionStorage is tab-isolated so
                // other tabs switching workspaces cannot overwrite this tab’s choice.
                // Survives reloads within the same tab session.
                try {
                    sessionStorage.setItem("session_workspace", JSON.stringify(workspace));
                } catch (e) {
                    console.warn("[WorkspaceContext] Failed to write session_workspace to sessionStorage:", e);
                }

                // ✅ Global cache for offline boot and new-tab default.
                try {
                    localStorage.setItem("cached_workspace", JSON.stringify(workspace));
                } catch (e) {
                    console.warn("[WorkspaceContext] Failed to cache workspace to localStorage:", e);
                }

                //fetch the notifications

                //real-time notification
                // 2️⃣ Live notifications (future only)
                // setTimeout(() => {
                //handleRealTimeNotification(workspace._id);
                // }, 0); // async, doesn’t block UI
            }
            else {
                console.warn("Workspace not found !")
            }
        }
        catch (err) {
            console.error("Error selecting workspace ", err);
        }
    }

    useEffect(() => {
        // Skip API calls on form pages
        if (isFormPage) return;

        // Wait for the synchronous Auth -> User fetch chain to complete before firing Workspace fetch
        if (!isAuthenticated || !user || !user.organizationId) {
            return;
        }

        const init = async () => {
            // ─── Step 0: Restore workspace identity instantly (no network) ─────────
            // Priority order:
            //   1. sessionStorage  — tab-specific, survives reload, immune to other tabs
            //   2. cookie + localStorage — fallback for brand-new tabs only
            // This unblocks downstream hooks (useFetchRootNodes, sidebar) immediately,
            // even when the API call below is slow or offline.
            try {
                const sessionRaw = sessionStorage.getItem("session_workspace");
                if (sessionRaw) {
                    // This tab already has its own workspace — use it directly.
                    const sessionWorkspace = JSON.parse(sessionRaw) as Workspace;
                    setCurrentWorkspace(sessionWorkspace);
                } else {
                    // Brand-new tab: no sessionStorage yet — fall back to cookie + localStorage.
                    const savedId = getWorkspaceIdFromCookie();
                    const cached = localStorage.getItem("cached_workspace");
                    if (cached) {
                        const cachedWorkspace = JSON.parse(cached) as Workspace;
                        // Only use cache if it matches the cookie-selected workspace
                        if (!savedId || cachedWorkspace._id === savedId) {
                            setCurrentWorkspace(cachedWorkspace);
                        }
                    }
                }
            } catch (e) {
                console.warn("[WorkspaceContext] Failed to read cached workspace:", e);
            }

            // ─── Step 1: Fetch fresh from API (background refresh) ───────────────
            try {
                const data = await fetchWorkspaces();
                setWorkspaces(data);

                // Resolve which workspace to activate from the API result.
                // sessionStorage is the authority for an existing tab — it is
                // tab-isolated so other tabs cannot interfere with it.
                // Cookie is the fallback for brand-new tabs that have no session yet.
                let savedId: string | null = null;
                try {
                    const sessionRaw = sessionStorage.getItem("session_workspace");
                    if (sessionRaw) {
                        savedId = (JSON.parse(sessionRaw) as Workspace)._id ?? null;
                    }
                } catch { /* ignore parse errors */ }

                if (!savedId) {
                    savedId = getWorkspaceIdFromCookie();
                }

                if (savedId) {
                    const found = data.find((ws) => ws._id === savedId);
                    if (found) {
                        handleSelectedWorkspace(found); // refreshes React state + sessionStorage + localStorage
                    } else {
                        console.warn(`Workspace with ID ${savedId} not found in fetched workspaces.`);
                    }
                } else {
                    console.log("No workspace ID found in sessionStorage or cookie");
                }
            } catch (error) {
                console.error("[WorkspaceContext] Error fetching workspace from API (may be offline):", error);
                // Silently fall back — cached workspace is already set above
            }
        };

        init();
        // Depend only on the identity fields this effect actually uses, NOT the
        // whole `user` object — otherwise a profile edit (name/image/about) makes
        // a new `user` reference and needlessly refetches workspaces, which
        // resets currentWorkspace and the whole UI.
    }, [isFormPage, isAuthenticated, user?.email, user?.organizationId]);

    useEffect(() => {
        //fetch the current workspace member
        const members: Members[] = currentWorkspace?.members || [];
        // Merge instead of replacing: the raw workspace doc has no
        // image/about/cover, so a blind reset would wipe data the members API
        // (and the live-user patch) already enriched. Carry those fields over
        // for members we already know by id/email.
        setWorkspaceMembers((prev) => {
            // An empty doc array only ever means a minimal/stale workspace
            // object (a real workspace always contains at least the current
            // user) — don't let it wipe an already-populated directory.
            if (members.length === 0) return prev;
            if (prev.length === 0) return members;
            const byId = new Map<string, Members>();
            const byEmail = new Map<string, Members>();
            for (const m of prev) {
                if (m.userId) byId.set(String(m.userId), m);
                if (m.userEmail) byEmail.set(m.userEmail.toLowerCase(), m);
            }
            return members.map((m) => {
                const existing =
                    byId.get(String(m.userId)) ||
                    byEmail.get((m.userEmail || "").toLowerCase());
                if (!existing) return m;
                return {
                    ...existing,
                    ...m,
                    // Preserve enriched fields the raw doc doesn't carry.
                    userName: m.userName || existing.userName,
                    image: m.image ?? existing.image,
                    about: m.about ?? existing.about,
                    coverUrl: m.coverUrl ?? existing.coverUrl,
                };
            });
        });

        // Fetch groups from workspace and normalize IDs
        const groups = (currentWorkspace?.groups || []).map((group: any) => ({
            ...group,
            id: group.id || String(group._id),
        }));
        setWorkspaceGroups(groups);
    }, [currentWorkspace])

    // Once the workspace is known, fetch the member directory and enrich each
    // existing member in place with their profile (image/about/cover).
    useEffect(() => {
        const wsId = currentWorkspace?._id;
        if (!wsId || isFormPage) return;
        let cancelled = false;
        (async () => {
            const res = await getWithAuth<{ members?: ApiMember[] }>(
                `/api/user/workspace/${wsId}/members`,
            );
            if (cancelled || !res || "isError" in res || !Array.isArray(res.members)) return;
            const apiMembers = res.members;

            // Rebuild the directory to exactly the returned member set (drops
            // removed members); reuse known profile fields as fallbacks only.
            setWorkspaceMembers((prev) => {
                const prevById = new Map<string, Members>();
                const prevByEmail = new Map<string, Members>();
                for (const m of prev) {
                    if (m.userId) prevById.set(String(m.userId), m);
                    if (m.userEmail) prevByEmail.set(m.userEmail.toLowerCase(), m);
                }
                return apiMembers
                    .filter((p) => p.id || p.email)
                    .map((p) => {
                        const existing =
                            (p.id ? prevById.get(String(p.id)) : undefined) ||
                            (p.email ? prevByEmail.get(p.email.toLowerCase()) : undefined);
                        return {
                            userId: p.id ? String(p.id) : existing?.userId || "",
                            userEmail: p.email || existing?.userEmail || "",
                            userName: p.name || existing?.userName || "",
                            role: p.role || existing?.role || "member",
                            joinedAt: p.joinedAt || existing?.joinedAt || "",
                            image: p.image || existing?.image,
                            about: p.about ?? existing?.about,
                            coverUrl: p.coverUrl ?? existing?.coverUrl,
                        };
                    });
            });
        })();
        return () => { cancelled = true; };
        // Also keyed on the doc's member count: the directory refetches when
        // the full workspace doc lands after the minimal fallback (same _id),
        // and when members join/leave.
    }, [currentWorkspace?._id, currentWorkspace?.members?.length, isFormPage])

    // Live membership/role changes (v2 socket events). socketEventRouter emits
    // workspace:members-invalidate (someone else's membership changed in a
    // workspace I'm in) and workspace:list-invalidate (MY access changed —
    // added to or removed from a workspace). Both are satisfied by re-pulling
    // the workspace list: it refreshes currentWorkspace.members (→ the member
    // list, avatars, mention pickers) and the switcher (a revoked workspace
    // disappears, a granted one appears).
    useEffect(() => {
        if (isFormPage) return;
        let inFlight = false;
        const refresh = async () => {
            if (inFlight) return;
            inFlight = true;
            try {
                const data = await fetchWorkspaces();
                setWorkspaces(data);

                const current = currentWorkspaceRef.current;
                if (current?._id && Array.isArray(data)) {
                    const fresh = data.find(
                        (w: Workspace) => String(w._id) === String(current._id),
                    );

                    if (fresh) {
                        // Refresh the ACTIVE workspace, not just the switcher
                        // list. workspaceMembers — and therefore the People
                        // list, avatars and mention pickers — is derived from
                        // currentWorkspace.members, so updating only
                        // `workspaces` left the member list stale after someone
                        // was added, removed or had their role changed.
                        setCurrentWorkspace(fresh);
                    } else {
                        // MY access to the workspace I'm in was revoked — it is
                        // simply gone from the refreshed list. Don't leave the
                        // user sitting inside a workspace they can no longer
                        // read (every subsequent fetch would 403).
                        const next = data[0] ?? null;
                        toast.error(`You no longer have access to "${current.name}".`);
                        if (next && switchWorkspaceRef.current) {
                            switchWorkspaceRef.current(next);
                        } else {
                            setCurrentWorkspace(null);
                        }
                        router.push("/notes/home");
                    }
                }
            } catch (e) {
                console.error("[WorkspaceContext] live membership refresh failed:", e);
            } finally {
                inFlight = false;
            }
        };
        eventBus.on("workspace:members-invalidate", refresh);
        eventBus.on("workspace:list-invalidate", refresh);
        eventBus.on("socket:reconnected", refresh);
        return () => {
            eventBus.off("workspace:members-invalidate", refresh);
            eventBus.off("workspace:list-invalidate", refresh);
            eventBus.off("socket:reconnected", refresh);
        };
    }, [isFormPage])

    // Keep the current user's own entry in the directory in sync with their
    // live profile. The directory only reloads on workspace switch, so without
    // this a profile edit (name/image/about/cover) wouldn't show up in the
    // synced spots (mentions/comments/notifications/view cards) until then.
    useEffect(() => {
        if (!user) return;
        const uid = user.id ? String(user.id) : undefined;
        const uemail = (user.email || "").toLowerCase();
        if (!uid && !uemail) return;
        setWorkspaceMembers((prev) => {
            let changed = false;
            const next = prev.map((m) => {
                const isMe =
                    (uid && String(m.userId) === uid) ||
                    (uemail && (m.userEmail || "").toLowerCase() === uemail);
                if (!isMe) return m;
                const patched: Members = {
                    ...m,
                    userName: user.name || m.userName,
                    // `?? m.image` (not `|| undefined`): keep the enriched image
                    // when user.image is transiently undefined; "" still clears it.
                    image: user.image ?? m.image,
                };
                if (
                    patched.userName !== m.userName ||
                    patched.image !== m.image
                ) {
                    changed = true;
                    return patched;
                }
                return m;
            });
            return changed ? next : prev;
        });
    }, [user?.id, user?.email, user?.name, user?.image])

    // Lookups over the single workspaceMembers list (used where only an
    // id/email is known, e.g. notification avatars).
    const memberIndex = useMemo(() => {
        const byId = new Map<string, Members>();
        const byEmail = new Map<string, Members>();
        for (const m of workspaceMembers) {
            if (m.userId) byId.set(String(m.userId), m);
            if (m.userEmail) byEmail.set(m.userEmail.toLowerCase(), m);
        }
        return { byId, byEmail };
    }, [workspaceMembers])

    const getMemberImage = useCallback(
        (idOrEmail?: string): string | undefined => {
            if (!idOrEmail) return undefined;
            const m =
                memberIndex.byId.get(idOrEmail) ||
                memberIndex.byEmail.get(idOrEmail.toLowerCase());
            return m?.image || undefined;
        },
        [memberIndex],
    )

    // Live name from the directory (callers use their stored name as fallback).
    const getMemberName = useCallback(
        (idOrEmail?: string): string | undefined => {
            if (!idOrEmail) return undefined;
            const m =
                memberIndex.byId.get(idOrEmail) ||
                memberIndex.byEmail.get(idOrEmail.toLowerCase());
            return m?.userName || undefined;
        },
        [memberIndex],
    )

    // Resolve a userId from an id or email — used where only the email is known
    // (e.g. comment authors) but a userId is needed for a /userprofile link.
    const getMemberId = useCallback(
        (idOrEmail?: string): string | undefined => {
            if (!idOrEmail) return undefined;
            const m =
                memberIndex.byId.get(idOrEmail) ||
                memberIndex.byEmail.get(idOrEmail.toLowerCase());
            return m?.userId ? String(m.userId) : undefined;
        },
        [memberIndex],
    )

    const allWorkspaceMemberName = useMemo(
        () => workspaceMembers.map((m) => m.userName),
        [workspaceMembers],
    )

    const refreshWorkspaceGroups = useCallback(() => {
        if (currentWorkspace) {
            const groups = ((currentWorkspace)?.groups || []).map((group: any) => ({
                ...group,
                id: group.id || String(group._id),
            }));
            setWorkspaceGroups(groups);
        }
    }, [currentWorkspace]);

    const createWorkspaceGroup = async (name: string, members: Members[] = []): Promise<WorkspaceGroup | null> => {
        if (!currentWorkspace?._id) {
            toast.error("No workspace selected");
            return null;
        }

        try {
            const response = await createGroup({
                workspaceId: currentWorkspace._id,
                name,
                members,
            });

            // Get the updated groups from the workspace response and normalize IDs
            const updatedGroups = (response.groups || []).map((group: any) => ({
                ...group,
                id: group.id || String(group._id),
            }));
            setWorkspaceGroups(updatedGroups);

            // Update workspace in state
            if (currentWorkspace) {
                setCurrentWorkspace({
                    ...currentWorkspace,
                    groups: updatedGroups,
                } as Workspace);
            }

            // Find and return the newly created group (last one in the array)
            const newGroup = updatedGroups[updatedGroups.length - 1];
            toast.success("Group created successfully");
            return newGroup || null;
        } catch (error) {
            console.error("Error creating group:", error);
            toast.error(error instanceof Error ? error.message : "Failed to create group");
            return null;
        }
    };

    const updateWorkspaceGroup = async (groupId: string, name?: string, members?: Members[]): Promise<void> => {
        if (!currentWorkspace?._id) {
            toast.error("No workspace selected");
            return;
        }

        try {
            const response = await updateGroup({
                workspaceId: currentWorkspace._id,
                groupId,
                name,
                members,
            });

            // Get the updated groups from the workspace response and normalize IDs
            const updatedGroups = (response.groups || []).map((group: any) => ({
                ...group,
                id: group.id || String(group._id),
            }));
            setWorkspaceGroups(updatedGroups);

            // Update workspace in state
            if (currentWorkspace) {
                setCurrentWorkspace({
                    ...currentWorkspace,
                    groups: updatedGroups,
                } as Workspace);
            }

            toast.success("Group updated successfully");
        } catch (error) {
            console.error("Error updating group:", error);
            toast.error(error instanceof Error ? error.message : "Failed to update group");
        }
    };

    const deleteWorkspaceGroup = async (groupId: string): Promise<void> => {
        if (!currentWorkspace?._id) {
            toast.error("No workspace selected");
            return;
        }

        try {
            const response = await deleteGroup({
                workspaceId: currentWorkspace._id,
                groupId,
            });

            // Get the updated groups from the workspace response and normalize IDs
            const updatedGroups = (response.groups || []).map((group: any) => ({
                ...group,
                id: group.id || String(group._id),
            }));
            setWorkspaceGroups(updatedGroups);

            // Update workspace in state
            if (currentWorkspace) {
                setCurrentWorkspace({
                    ...currentWorkspace,
                    groups: updatedGroups,
                } as Workspace);
            }

            toast.success("Group deleted successfully");
        } catch (error) {
            console.error("Error deleting group:", error);
            toast.error(error instanceof Error ? error.message : "Failed to delete group");
        }
    };

    //Real time Notification
    // useEffect(() => {
    //     console.log("1111111---->")
    //     console.log("🔔 SSE connection opened -------->", currentWorkspace?._id);
    //     if (!currentWorkspace?._id) return;

    //     const eventSource = new EventSource(
    //       `/api/work-space/notification`
    //     );

    //     eventSource.onmessage = (event) => {
    //       try {
    //         const data = JSON.parse(event.data);
    //         console.log("🔔 New Notification SSE ->", data);

    //         setWorkspaceNotifications((prev) => [
    //           data.notification,
    //           ...prev, // prepend so newest shows first
    //         ]);
    //       } catch (err) {
    //         console.error("Failed to parse SSE notification", err);
    //       }
    //     };

    //     eventSource.onerror = (err) => {
    //       console.error("SSE connection error:", err);
    //       eventSource.close();
    //     };

    //     eventSource.onopen = () => {
    //         console.log("✅ SSE connection opened");
    //     };

    //     return () => {
    //       console.log("🔌 Closing SSE connection");
    //       eventSource.close();
    //     };
    // }, []); 


    // Always-latest handles for the live-membership effect below, which must
    // not re-subscribe whenever the current workspace changes.
    currentWorkspaceRef.current = currentWorkspace;

    const switchWorkspace = (workspace: Workspace, destination?: string) => {
        // Post-switch destination for the notes-layout redirect (null = its default).
        pendingSwitchDestinationRef.current = destination ?? null;
        localStorage.setItem("selectedWorkspaceName", workspace.name);
        const encrypted = CryptoJS.AES.encrypt(
            JSON.stringify({ workspaceId: workspace._id }),
            SECRET_KEY
        ).toString();
        document.cookie = `workspace=${encodeURIComponent(encrypted)}; path=/; max-age=${60 * 60 * 24}; SameSite=Lax`;
        handleSelectedWorkspace(workspace);
    };
    switchWorkspaceRef.current = switchWorkspace;

    const fetchAllWorkspace = async () => {
        const data = await fetchWorkspaces();
        setWorkspaces(data);
    };

    const contextValue: workspaceContextType = {
        name,
        workspaces,
        fetchAllWorkspace,
        setCurrentWorkspace,
        workspaceMembers,
        getMemberImage,
        getMemberName,
        getMemberId,
        handleSelectedWorkspace,
        switchWorkspace,
        pendingSwitchDestinationRef,
        workspaceNotifications,
        allWorkspaceMemberName,
        currentWorkspace,
        workspaceGroups,
        refreshWorkspaceGroups,
        createWorkspaceGroup,
        updateWorkspaceGroup,
        deleteWorkspaceGroup,
    };

    latestWorkspaceContext = contextValue;

    return <WorkspaceContext.Provider value={contextValue}>
        {children}
    </WorkspaceContext.Provider>
}

// The provider's most recent value, for useWorkspaceContextWithFallback.
let latestWorkspaceContext: workspaceContextType | undefined;

export function useWorkspaceContext() {
    const context = useContext(WorkspaceContext)
    if (context === undefined) {
        throw new Error("useWorkspaceContext must be used within a workspaceProvider");
    }
    return context;
}

/**
 * Like useWorkspaceContext, but falls back to the provider's latest value
 * when this tree has no provider (detached React roots). The fallback is a
 * snapshot — not reactive — which is fine for short-lived UI (e.g. the emoji
 * picker) that re-reads it on every open. Returns undefined only when no
 * provider has mounted at all.
 */
export function useWorkspaceContextWithFallback() {
    return useContext(WorkspaceContext) ?? latestWorkspaceContext;
}
